import * as THREE from 'three';
import * as d3 from 'd3-geo';

export class Globe {
    private container: HTMLElement;
    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private globe!: THREE.Mesh;
    private frameId: number | null = null;

    // Configuration
    private readonly GLOBE_RADIUS = 10;
    private readonly ROTATION_SPEED = 0.002;
    private readonly CANVAS_WIDTH = 2048;
    private readonly CANVAS_HEIGHT = 1024;

    // Colors (Vector Style)
    private COLORS = {
        WATER: '#90d5ff',
        LAND: '#17C3B2',
        CITY: '#333333',
        LANDMARK: '#ff6b6b'
    };

    constructor(container: HTMLElement) {
        this.container = container;
        this.updateColorsFromCSS();
        this.init();
    }

    private updateColorsFromCSS() {
        const styles = getComputedStyle(document.documentElement);
        const water = styles.getPropertyValue('--globe-water').trim();
        const land = styles.getPropertyValue('--globe-land').trim();

        if (water) this.COLORS.WATER = water;
        if (land) this.COLORS.LAND = land;
        this.COLORS.CITY = styles.getPropertyValue('--globe-city');
        this.COLORS.LANDMARK = styles.getPropertyValue('--globe-landmark');
    }

    private isDragging = false;
    private previousMouseX = 0;
    private previousMouseY = 0;

    private async init() {
        // 1. Setup Three.js Scene
        const width = this.container.clientWidth;
        const height = this.container.clientHeight || width; // Fallback if height is 0

        this.scene = new THREE.Scene();
        this.scene.background = null; // Transparent

        this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
        this.camera.position.z = 27;

        this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.container.appendChild(this.renderer.domElement);

        // 2. Create Globe
        await this.createGlobe();

        this.globe.rotation.x = 0.5;
        this.globe.rotation.y = -2;
        // this.globe.rotation.z = 0.5;

        // 3. Add Lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
        // const ambientLight = new THREE.AmbientLight(0xffffff, 0.6); // Increased ambient light
        this.scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 4);
        // const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(10, 10, 18);
        this.scene.add(directionalLight);

        // 4. Handle Resize
        window.addEventListener('resize', this.onResize.bind(this));

        // 5. Handle Interaction
        this.container.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.container.addEventListener('mousemove', this.onMouseMove.bind(this));
        window.addEventListener('mouseup', this.onMouseUp.bind(this));

        this.container.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
        this.container.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        window.addEventListener('touchend', this.onTouchEnd.bind(this));

        // 6. Start Animation
        this.animate();
    }

    private async createGlobe() {
        // Fetch GeoJSON
        const response = await fetch('/globe/ne_110m_land.geojson');
        const data = await response.json();

        // Draw Map to Canvas
        const canvas = document.createElement('canvas');
        canvas.width = this.CANVAS_WIDTH;
        canvas.height = this.CANVAS_HEIGHT;
        const context = canvas.getContext('2d');

        if (!context) return;

        // Projection
        const projection = d3.geoEquirectangular()
            .translate([this.CANVAS_WIDTH / 2, this.CANVAS_HEIGHT / 2])
            .scale(this.CANVAS_WIDTH / (2 * Math.PI));

        const path = d3.geoPath(projection, context);

        // Background (Water)
        context.fillStyle = this.COLORS.WATER;
        context.fillRect(0, 0, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);

        // Continents (Land)
        context.fillStyle = this.COLORS.LAND;
        context.beginPath();
        path(data);
        context.fill();

        // Create Texture
        const texture = new THREE.CanvasTexture(canvas);

        // Create Sphere Mesh
        const geometry = new THREE.SphereGeometry(this.GLOBE_RADIUS, 64, 64);
        // MeshBasicMaterial
        const material = new THREE.MeshPhongMaterial({
            map: texture,
            transparent: true,
            shininess: 5
        });

        this.globe = new THREE.Mesh(geometry, material);
        this.scene.add(this.globe);

        // Add Markers
        this.addMarkers();
    }

    private cityMarkers: { mesh: THREE.Mesh, landmark: boolean }[] = [];

    private addMarkers() {
        // Major Cities Data (Lat, Lon, IsLandmark)
        // Population Rule: Country Pop / 5M = Num Cities
        // USA: ~67, UK: ~14, Canada: ~8, Australia: ~5, NZ: ~1
        const cities = [
            // USA (67)
            { name: 'New York', lat: 40.7128, lon: -74.0060, landmark: true },
            { name: 'Los Angeles', lat: 34.0522, lon: -118.2437, landmark: true },
            { name: 'Chicago', lat: 41.8781, lon: -87.6298, landmark: true },
            { name: 'Houston', lat: 29.7604, lon: -95.3698, landmark: false },
            { name: 'Phoenix', lat: 33.4484, lon: -112.0740, landmark: false },
            { name: 'Philadelphia', lat: 39.9526, lon: -75.1652, landmark: false },
            { name: 'San Antonio', lat: 29.4241, lon: -98.4936, landmark: false },
            { name: 'San Diego', lat: 32.7157, lon: -117.1611, landmark: false },
            { name: 'Dallas', lat: 32.7767, lon: -96.7970, landmark: false },
            { name: 'San Jose', lat: 37.3382, lon: -121.8863, landmark: false },
            { name: 'Austin', lat: 30.2672, lon: -97.7431, landmark: false },
            { name: 'Jacksonville', lat: 30.3322, lon: -81.6557, landmark: false },
            { name: 'Fort Worth', lat: 32.7555, lon: -97.3308, landmark: false },
            { name: 'Columbus', lat: 39.9612, lon: -82.9988, landmark: false },
            { name: 'Indianapolis', lat: 39.7684, lon: -86.1581, landmark: false },
            { name: 'Charlotte', lat: 35.2271, lon: -80.8431, landmark: false },
            { name: 'San Francisco', lat: 37.7749, lon: -122.4194, landmark: true },
            { name: 'Seattle', lat: 47.6062, lon: -122.3321, landmark: true },
            { name: 'Denver', lat: 39.7392, lon: -104.9903, landmark: false },
            { name: 'Washington', lat: 38.9072, lon: -77.0369, landmark: true },
            { name: 'Nashville', lat: 36.1627, lon: -86.7816, landmark: false },
            { name: 'Oklahoma City', lat: 35.4676, lon: -97.5164, landmark: false },
            { name: 'El Paso', lat: 31.7619, lon: -106.4850, landmark: false },
            { name: 'Boston', lat: 42.3601, lon: -71.0589, landmark: false },
            { name: 'Portland', lat: 45.5152, lon: -122.6784, landmark: false },
            { name: 'Las Vegas', lat: 36.1699, lon: -115.1398, landmark: false },
            { name: 'Detroit', lat: 42.3314, lon: -83.0458, landmark: false },
            { name: 'Memphis', lat: 35.1495, lon: -90.0490, landmark: false },
            { name: 'Louisville', lat: 38.2527, lon: -85.7585, landmark: false },
            { name: 'Baltimore', lat: 39.2904, lon: -76.6122, landmark: false },
            { name: 'Milwaukee', lat: 43.0389, lon: -87.9065, landmark: false },
            { name: 'Albuquerque', lat: 35.0844, lon: -106.6504, landmark: false },
            { name: 'Tucson', lat: 32.2226, lon: -110.9747, landmark: false },
            { name: 'Fresno', lat: 36.7378, lon: -119.7871, landmark: false },
            { name: 'Sacramento', lat: 38.5816, lon: -121.4944, landmark: false },
            { name: 'Kansas City', lat: 39.0997, lon: -94.5786, landmark: false },
            { name: 'Mesa', lat: 33.4152, lon: -111.8315, landmark: false },
            { name: 'Atlanta', lat: 33.7490, lon: -84.3880, landmark: false },
            { name: 'Omaha', lat: 41.2565, lon: -95.9345, landmark: false },
            { name: 'Colorado Springs', lat: 38.8339, lon: -104.8214, landmark: false },
            { name: 'Raleigh', lat: 35.7796, lon: -78.6382, landmark: false },
            { name: 'Long Beach', lat: 33.7701, lon: -118.1937, landmark: false },
            { name: 'Virginia Beach', lat: 36.8529, lon: -75.9780, landmark: false },
            { name: 'Miami', lat: 25.7617, lon: -80.1918, landmark: true },
            { name: 'Oakland', lat: 37.8044, lon: -122.2711, landmark: false },
            { name: 'Minneapolis', lat: 44.9778, lon: -93.2650, landmark: false },
            { name: 'Tulsa', lat: 36.1540, lon: -95.9928, landmark: false },
            { name: 'Arlington', lat: 32.7357, lon: -97.1081, landmark: false },
            { name: 'Tampa', lat: 27.9506, lon: -82.4572, landmark: false },
            { name: 'New Orleans', lat: 29.9511, lon: -90.0715, landmark: false },
            { name: 'Wichita', lat: 37.6872, lon: -97.3301, landmark: false },
            { name: 'Cleveland', lat: 41.4993, lon: -81.6944, landmark: false },
            { name: 'Bakersfield', lat: 35.3733, lon: -119.0187, landmark: false },
            { name: 'Aurora', lat: 39.7294, lon: -104.8319, landmark: false },
            { name: 'Anaheim', lat: 33.8366, lon: -117.9143, landmark: false },
            { name: 'Honolulu', lat: 21.3069, lon: -157.8583, landmark: false },
            { name: 'Santa Ana', lat: 33.7455, lon: -117.8677, landmark: false },
            { name: 'Riverside', lat: 33.9806, lon: -117.3755, landmark: false },
            { name: 'Corpus Christi', lat: 27.8006, lon: -97.3964, landmark: false },
            { name: 'Lexington', lat: 38.0406, lon: -84.5037, landmark: false },
            { name: 'Stockton', lat: 37.9577, lon: -121.2908, landmark: false },
            { name: 'Henderson', lat: 36.0395, lon: -114.9817, landmark: false },
            { name: 'Saint Paul', lat: 44.9537, lon: -93.0900, landmark: false },
            { name: 'St. Louis', lat: 38.6270, lon: -90.1994, landmark: false },
            { name: 'Cincinnati', lat: 39.1031, lon: -84.5120, landmark: false },
            { name: 'Pittsburgh', lat: 40.4406, lon: -79.9959, landmark: false },
            { name: 'Greensboro', lat: 36.0726, lon: -79.7920, landmark: false },

            // UK (14)
            { name: 'London', lat: 51.5074, lon: -0.1278, landmark: true },
            { name: 'Birmingham', lat: 52.4862, lon: -1.8904, landmark: false },
            { name: 'Manchester', lat: 53.4808, lon: -2.2426, landmark: false },
            { name: 'Glasgow', lat: 55.8642, lon: -4.2518, landmark: false },
            { name: 'Leeds', lat: 53.8008, lon: -1.5491, landmark: false },
            { name: 'Liverpool', lat: 53.4084, lon: -2.9916, landmark: false },
            { name: 'Newcastle', lat: 54.9783, lon: -1.6178, landmark: false },
            { name: 'Sheffield', lat: 53.3811, lon: -1.4701, landmark: false },
            { name: 'Bristol', lat: 51.4545, lon: -2.5879, landmark: false },
            { name: 'Belfast', lat: 54.5973, lon: -5.9301, landmark: false },
            { name: 'Edinburgh', lat: 55.9533, lon: -3.1883, landmark: false },
            { name: 'Brighton', lat: 50.8225, lon: -0.1372, landmark: false },
            { name: 'Leicester', lat: 52.6369, lon: -1.1398, landmark: false },
            { name: 'Cardiff', lat: 51.4816, lon: -3.1791, landmark: false },

            // Canada (8)
            { name: 'Toronto', lat: 43.6532, lon: -79.3832, landmark: true },
            { name: 'Montreal', lat: 45.5017, lon: -73.5673, landmark: false },
            { name: 'Vancouver', lat: 49.2827, lon: -123.1207, landmark: false },
            { name: 'Calgary', lat: 51.0447, lon: -114.0719, landmark: false },
            { name: 'Edmonton', lat: 53.5461, lon: -113.4938, landmark: false },
            { name: 'Ottawa', lat: 45.4215, lon: -75.6972, landmark: false },
            { name: 'Winnipeg', lat: 49.8951, lon: -97.1384, landmark: false },
            { name: 'Quebec City', lat: 46.8139, lon: -71.2080, landmark: false },

            // Australia (5)
            { name: 'Sydney', lat: -33.8688, lon: 151.2093, landmark: true },
            { name: 'Melbourne', lat: -37.8136, lon: 144.9631, landmark: false },
            { name: 'Brisbane', lat: -27.4705, lon: 153.0260, landmark: false },
            { name: 'Perth', lat: -31.9505, lon: 115.8605, landmark: false },
            { name: 'Adelaide', lat: -34.9285, lon: 138.6007, landmark: false },

            // New Zealand (1)
            { name: 'Auckland', lat: -36.8485, lon: 174.7633, landmark: false },

            // EU (20)
            { name: 'Paris', lat: 48.8566, lon: 2.3522, landmark: true },
            { name: 'Berlin', lat: 52.5200, lon: 13.4050, landmark: true },
            { name: 'Madrid', lat: 40.4168, lon: -3.7038, landmark: true },
            { name: 'Rome', lat: 41.9028, lon: 12.4964, landmark: true },
            { name: 'Amsterdam', lat: 52.3676, lon: 4.9041, landmark: false },
            { name: 'Brussels', lat: 50.8503, lon: 4.3517, landmark: false },
            { name: 'Vienna', lat: 48.2082, lon: 16.3738, landmark: false },
            { name: 'Lisbon', lat: 38.7223, lon: -9.1393, landmark: false },
            { name: 'Stockholm', lat: 59.3293, lon: 18.0686, landmark: false },
            { name: 'Copenhagen', lat: 55.6761, lon: 12.5683, landmark: false },
            { name: 'Dublin', lat: 53.3498, lon: -6.2603, landmark: false },
            { name: 'Helsinki', lat: 60.1699, lon: 24.9384, landmark: false },
            { name: 'Warsaw', lat: 52.2297, lon: 21.0122, landmark: false },
            { name: 'Prague', lat: 50.0755, lon: 14.4378, landmark: false },
            { name: 'Budapest', lat: 47.4979, lon: 19.0402, landmark: false },
            { name: 'Athens', lat: 37.9838, lon: 23.7275, landmark: false },
            { name: 'Bucharest', lat: 44.4268, lon: 26.1025, landmark: false },
            { name: 'Sofia', lat: 42.6977, lon: 23.3219, landmark: false },
            { name: 'Munich', lat: 48.1351, lon: 11.5820, landmark: false },
            { name: 'Barcelona', lat: 41.3851, lon: 2.1734, landmark: false },

            // Middle East (1)
            { name: 'Tel Aviv', lat: 32.0853, lon: 34.7818, landmark: true },

            // Asia (8)
            { name: 'Tokyo', lat: 35.6762, lon: 139.6503, landmark: true },
            { name: 'Beijing', lat: 39.9042, lon: 116.4074, landmark: true },
            { name: 'Shanghai', lat: 31.2304, lon: 121.4737, landmark: true },
            { name: 'Seoul', lat: 37.5665, lon: 126.9780, landmark: false },
            { name: 'Bangkok', lat: 13.7563, lon: 100.5018, landmark: false },
            { name: 'Singapore', lat: 1.3521, lon: 103.8198, landmark: false },
            { name: 'Mumbai', lat: 19.0760, lon: 72.8777, landmark: false },
            { name: 'Dubai', lat: 25.2048, lon: 55.2708, landmark: true },

            // North Africa (2)
            { name: 'Cairo', lat: 30.0444, lon: 31.2357, landmark: false },
            { name: 'Casablanca', lat: 33.5731, lon: -7.5898, landmark: false },

            // Mexico (1)
            { name: 'Mexico City', lat: 19.4326, lon: -99.1332, landmark: true },

            // South America (5)
            { name: 'Sao Paulo', lat: -23.5505, lon: -46.6333, landmark: true },
            { name: 'Buenos Aires', lat: -34.6037, lon: -58.3816, landmark: false },
            { name: 'Rio de Janeiro', lat: -22.9068, lon: -43.1729, landmark: true },
            { name: 'Bogota', lat: 4.7110, lon: -74.0721, landmark: false },
            { name: 'Lima', lat: -12.0464, lon: -77.0428, landmark: false }
        ];

        const cityGeometry = new THREE.CircleGeometry(0.15, 8);
        const landmarkGeometry = new THREE.CircleGeometry(0.25, 10); // Slightly larger

        const cityMaterial = new THREE.MeshBasicMaterial({ color: this.COLORS.CITY });
        const landmarkMaterial = new THREE.MeshBasicMaterial({ color: this.COLORS.LANDMARK });

        cities.forEach(city => {
            const phi = (90 - city.lat) * (Math.PI / 180);
            const theta = (city.lon + 180) * (Math.PI / 180);

            const x = -(this.GLOBE_RADIUS * Math.sin(phi) * Math.cos(theta));
            const z = (this.GLOBE_RADIUS * Math.sin(phi) * Math.sin(theta));
            const y = (this.GLOBE_RADIUS * Math.cos(phi));

            const mesh = new THREE.Mesh(
                city.landmark ? landmarkGeometry : cityGeometry,
                city.landmark ? landmarkMaterial : cityMaterial
            );

            mesh.position.set(x, y, z);
            mesh.lookAt(new THREE.Vector3(0, 0, 0)); // Face outwards (actually looks at center, so we might need to flip or just use sphere/circle billboard)

            // Correct orientation: circles lay flat on the surface
            // lookAt(0,0,0) makes the Z axis of the circle point to center. 
            // CircleGeometry is in XY plane. So we want Z to point OUT.
            // mesh.lookAt(2*x, 2*y, 2*z) points it away from center.
            mesh.lookAt(new THREE.Vector3(2 * x, 2 * y, 2 * z));

            // Push it out slightly to avoid z-fighting
            mesh.position.multiplyScalar(1.005);

            this.globe.add(mesh);
            this.cityMarkers.push({ mesh, landmark: city.landmark });
        });
    }

    private onResize() {
        if (!this.container) return;
        const width = this.container.clientWidth;
        const height = this.container.clientHeight || width;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    private onMouseDown(event: MouseEvent) {
        this.isDragging = true;
        this.previousMouseX = event.clientX;
        this.previousMouseY = event.clientY;
    }

    private onMouseMove(event: MouseEvent) {
        if (!this.isDragging || !this.globe) return;

        const deltaX = event.clientX - this.previousMouseX;
        const deltaY = event.clientY - this.previousMouseY;

        this.previousMouseX = event.clientX;
        this.previousMouseY = event.clientY;

        this.globe.rotation.y += deltaX * 0.005;
        this.globe.rotation.x += deltaY * 0.005;
    }

    private onMouseUp() {
        this.isDragging = false;
    }

    private onTouchStart(event: TouchEvent) {
        if (event.touches.length === 1) {
            this.isDragging = true;
            this.previousMouseX = event.touches[0].clientX;
            this.previousMouseY = event.touches[0].clientY;
        }
    }

    private onTouchMove(event: TouchEvent) {
        if (!this.isDragging || !this.globe || event.touches.length !== 1) return;

        // Prevent scrolling while dragging globe
        if (event.cancelable) event.preventDefault();

        const deltaX = event.touches[0].clientX - this.previousMouseX;
        const deltaY = event.touches[0].clientY - this.previousMouseY;

        this.previousMouseX = event.touches[0].clientX;
        this.previousMouseY = event.touches[0].clientY;

        this.globe.rotation.y += deltaX * 0.005;
        this.globe.rotation.x += deltaY * 0.005;
    }

    private onTouchEnd() {
        this.isDragging = false;
    }

    private animate() {
        this.frameId = requestAnimationFrame(this.animate.bind(this));

        if (this.globe) {
            if (!this.isDragging) {
                // Calculate visibility score
                let score = 0;
                const vector = new THREE.Vector3();

                this.cityMarkers.forEach(marker => {
                    // Get world position
                    marker.mesh.getWorldPosition(vector);

                    // Check if visible (z > 0)
                    if (vector.z > 0) {
                        // Score based on Z (depth) - closer to camera (radius) is higher score
                        // Horizon (z=0) is 0 score. Center (z=radius) is 1 score.
                        const visibility = vector.z / this.GLOBE_RADIUS;
                        const weight = marker.landmark ? 3 : 1;
                        score += visibility * weight;
                    }
                });

                // Adjust speed based on score
                // Base speed 0.002
                // Slow down when score is high
                const baseSpeed = 0.002 * 100;
                const sensitivity = 0.05 * 20;
                const speed = baseSpeed / (1 + score * sensitivity);

                this.globe.rotation.y += speed;

                // Smoothly return X rotation to 0.5
                const targetX = 0.5;
                this.globe.rotation.x += (targetX - this.globe.rotation.x) * 0.05;
            }
        }

        this.renderer.render(this.scene, this.camera);
    }

    public destroy() {
        if (this.frameId) cancelAnimationFrame(this.frameId);
        window.removeEventListener('resize', this.onResize.bind(this));

        this.container.removeEventListener('mousedown', this.onMouseDown.bind(this));
        this.container.removeEventListener('mousemove', this.onMouseMove.bind(this));
        window.removeEventListener('mouseup', this.onMouseUp.bind(this));

        this.container.removeEventListener('touchstart', this.onTouchStart.bind(this));
        this.container.removeEventListener('touchmove', this.onTouchMove.bind(this));
        window.removeEventListener('touchend', this.onTouchEnd.bind(this));

        this.container.removeChild(this.renderer.domElement);
        this.renderer.dispose();
    }
}

export function initGlobe(el: HTMLElement) {
    new Globe(el);
}
